import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Buildings, Stack, FileText, Folder, UserCircle, MapPin, CaretRight } from "@phosphor-icons/react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { toast } from "sonner";

function Customer360({ cid, onClose }) {
  const [d, setD] = useState(null);
  const [contact, setContact] = useState({ name: "", role: "", email: "", phone: "" });
  const [site, setSite] = useState({ name: "", address: "", region: "", street: "", house_number: "", postal_code: "", city: "", state: "", country: "", lat: null, lon: null });
  const [siteOpen, setSiteOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/customers/${cid}/360`).then((r) => setD(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Skeleton className="h-40" />;
  const addContact = async () => { await api.post("/contacts", { customer_id: cid, ...contact }); setContact({ name: "", role: "", email: "", phone: "" }); load(); toast.success("Kontakt hinzugefügt"); };
  const addSite = async () => {
    if (!site.name) return;
    try {
      await api.post("/sites", {
        customer_id: cid, name: site.name, address: site.address || null, region: site.region || site.city || null,
        street: site.street || null, house_number: site.house_number || null, postal_code: site.postal_code || null,
        city: site.city || null, state: site.state || null, country: site.country || null,
        lat: site.lat ?? null, lon: site.lon ?? null,
      });
      toast.success("Standort angelegt"); setSite({ name: "", address: "", region: "", street: "", house_number: "", postal_code: "", city: "", state: "", country: "", lat: null, lon: null }); setSiteOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openSite = (sid) => { if (onClose) onClose(); navigate(`/standorte/${sid}`); };
  const Section = ({ icon: Icon, title, count, children }) => (
    <div><div className="mb-2 flex items-center gap-2"><Icon size={15} className="text-primary" /><Label2>{title} ({count})</Label2></div>{children}</div>
  );
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border p-3 text-sm">
        <div className="font-semibold">{d.customer.name} <span className="font-mono text-xs text-primary">{d.customer.customer_number}</span></div>
        <div className="text-muted-foreground">{d.customer.email || "—"} · {d.customer.phone || "—"} · {d.customer.address || "—"}</div>
        {d.customer.is_mirror && <StatusBadge tone="blue">Plancraft-Spiegel</StatusBadge>}
      </div>

      {/* Standorte des Kunden — kontextbasierte Erstellung */}
      <div className="rounded-md border border-border p-3" data-testid="customer-sites">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2"><MapPin size={15} className="text-primary" /><Label2>Standorte ({(d.sites || []).length})</Label2></div>
          <Button size="sm" variant="outline" className="h-7 gap-1.5" data-testid="customer-add-site-btn" onClick={() => setSiteOpen((v) => !v)}><Plus size={13} weight="bold" /> Neuer Standort</Button>
        </div>
        {siteOpen && (
          <div className="mb-3 space-y-2 rounded-md bg-muted/50 p-2">
            <Input placeholder="Name *" data-testid="customer-site-name" value={site.name} onChange={(e) => setSite((s) => ({ ...s, name: e.target.value }))} />
            <AddressAutocomplete testid="customer-site-address" value={site.address}
              onChange={(t) => setSite((s) => ({ ...s, address: t }))}
              onSelect={(a) => setSite((s) => ({ ...s, address: a.label, street: a.street, house_number: a.house_number, postal_code: a.postal_code, city: a.city, state: a.state, country: a.country, lat: a.lat, lon: a.lon }))} />
            {(site.postal_code || site.city) && <div className="text-xs text-muted-foreground">{[site.street, site.house_number].filter(Boolean).join(" ")} · {[site.postal_code, site.city].filter(Boolean).join(" ")} · {site.country || ""}</div>}
            <Button size="sm" data-testid="customer-site-save" onClick={addSite} disabled={!site.name}>Standort anlegen</Button>
          </div>
        )}
        <div className="divide-y divide-border">
          {(d.sites || []).length === 0 && <div className="py-2 text-sm text-muted-foreground">Noch keine Standorte.</div>}
          {(d.sites || []).map((s) => (
            <button key={s.id} data-testid={`customer-site-${s.id}`} onClick={() => openSite(s.id)}
              className="flex w-full items-center justify-between py-2 text-left text-sm hover:text-primary">
              <span>{s.name}{s.address ? <span className="text-muted-foreground"> · {s.address}</span> : ""}</span>
              <CaretRight size={14} />
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Section icon={Stack} title="Anlagen" count={d.assets.length}>{d.assets.slice(0, 5).map((a) => <div key={a.id} className="text-sm">› {a.name}</div>)}</Section>
        <Section icon={FileText} title="Verträge" count={d.contracts.length}>{d.contracts.map((c) => <div key={c.id} className="text-sm">› {c.title}</div>)}</Section>
        <Section icon={Folder} title="Fallakten" count={d.cases.length}>{d.cases.map((c) => <div key={c.id} className="text-sm">› {c.title}</div>)}</Section>
        <Section icon={UserCircle} title="Kontakte" count={d.contacts.length}>{d.contacts.map((c) => <div key={c.id} className="text-sm">› {c.name} ({c.role || "—"})</div>)}</Section>
      </div>
      <div className="rounded-md border border-border p-3">
        <Label2>Kontakt hinzufügen</Label2>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Input placeholder="Name" data-testid="contact-name" value={contact.name} onChange={(e) => setContact((s) => ({ ...s, name: e.target.value }))} />
          <Input placeholder="Rolle" value={contact.role} onChange={(e) => setContact((s) => ({ ...s, role: e.target.value }))} />
        </div>
        <Button size="sm" className="mt-2" data-testid="contact-add" onClick={addContact} disabled={!contact.name}>Hinzufügen</Button>
      </div>
    </div>
  );
}

export default function CRM() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", type: "gewerbe", email: "", phone: "", address: "", tags: [], notes: "" });

  const load = useCallback(() => api.get("/customers").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/customers", form); toast.success("Kunde angelegt"); setOpen(false); setForm({ name: "", type: "gewerbe", email: "", phone: "", address: "", tags: [], notes: "" }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!items) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="CRM · Kunden" subtitle={`${items.length} Kunden · Kontakte · Objekte · 360°-Sicht`}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-customer-button" className="gap-2"><Plus size={18} weight="bold" />Neuer Kunde</Button></DialogTrigger>
          <DialogContent data-testid="customer-dialog"><DialogHeader><DialogTitle className="font-heading">Kunde anlegen</DialogTitle></DialogHeader>
            <DialogDescription>Lokaler Kunde (CSP-Zusatzfelder).</DialogDescription>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label2>Name *</Label2><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Typ</Label2>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="gewerbe">Gewerbe</SelectItem><SelectItem value="privat">Privat</SelectItem></SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label2>E-Mail</Label2><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label2>Telefon</Label2><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              </div>
              <div className="space-y-1.5"><Label2>Adresse</Label2><Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="customer-save" onClick={submit} disabled={!form.name}>Anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="divide-y divide-border rounded-md border">
        {items.map((c) => (
          <div key={c.id} data-testid="customer-row" onClick={() => setDetail(c.id)} className="flex cursor-pointer items-center justify-between px-5 py-4 hover:bg-accent">
            <div className="flex items-center gap-3">
              <Buildings size={20} weight="duotone" className="text-primary" />
              <div><div className="font-semibold">{c.name}</div><div className="font-mono text-xs text-muted-foreground">{c.customer_number} · {c.type}</div></div>
            </div>
            <div className="flex items-center gap-2">{c.is_mirror && <StatusBadge tone="blue">Plancraft</StatusBadge>}{(c.tags || []).map((t) => <StatusBadge key={t} tone="gray">{t}</StatusBadge>)}</div>
          </div>
        ))}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="customer-360-dialog">
          <DialogHeader><DialogTitle className="font-heading">360°-Kundensicht</DialogTitle></DialogHeader>
          <DialogDescription>Kontakte, Anlagen, Verträge, Fälle.</DialogDescription>
          {detail && <Customer360 cid={detail} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
